import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { type AnalyticsData } from "@/lib/analytics-service"
import { Users, Activity, Clock, Play, TrendingUp, TrendingDown } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useStableHeight } from "@/hooks/use-stable-height"

interface AnalyticsCardsProps {
  data: AnalyticsData
}

export function AnalyticsCards({ data }: AnalyticsCardsProps) {
  // Хуки для стабилизации высоты каждой карточки
  const uniqueUsersHeight = useStableHeight('analytics-unique-users', data.isLoading, data.uniqueUsers !== null)
  const appLaunchesHeight = useStableHeight('analytics-app-launches', data.isLoading, data.appLaunches !== null)
  const avgTimeHeight = useStableHeight('analytics-avg-time', data.isLoading, data.avgTime !== null)
  const scenarioLaunchesHeight = useStableHeight('analytics-scenario-launches', data.isLoading, data.scenarioLaunches !== null)

  const formatPercent = (value: number): string => {
    const sign = value > 0 ? "+" : ""
    return `${sign}${value.toFixed(1)}%`
  }

  const computeChange = (current: number | null, prev: number | null) => {
    if (current === null || prev === null || prev === 0) {
      return { text: "0.0%", color: "text-muted-foreground", positive: null as null | boolean, prevText: "—" }
    }
    const delta = ((current - prev) / prev) * 100
    const positive = delta > 0 ? true : delta < 0 ? false : null
    const color = positive === null ? "text-muted-foreground" : positive ? "text-green-600" : "text-red-600"
    return { text: formatPercent(delta), color, positive, prevText: prev.toLocaleString("ru-RU") }
  }

  const uniqueUsersChange = computeChange(data.uniqueUsers, data.prevUniqueUsers)
  const appLaunchesChange = computeChange(data.appLaunches, data.prevAppLaunches)
  const avgTimeChange = computeChange(data.avgTime, data.prevAvgTime)
  const scenarioLaunchesChange = computeChange(data.scenarioLaunches, data.prevScenarioLaunches)
  const formatTime = (milliseconds: number | null): string => {
    if (milliseconds === null) return "—"
    const totalMinutes = milliseconds / (1000 * 60)
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    
    if (hours > 0) {
      const remainingMinutes = minutes.toFixed(1)
      return `${hours}ч ${remainingMinutes} мин`
    }
    return `${minutes.toFixed(1)} мин`
  }

  const formatNumber = (value: number | null): string => {
    if (value === null) return "—"
    return value.toLocaleString('ru-RU')
  }

  if (data.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
        <Card 
          ref={uniqueUsersHeight.ref}
          className="@container/card rounded-xl border shadow-none min-h-[140px]"
          style={uniqueUsersHeight.style}
        >
          <CardHeader className="space-y-3">
            <div className="w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </div>
            <div className="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </CardHeader>
        </Card>
        <Card 
          ref={appLaunchesHeight.ref}
          className="@container/card rounded-xl border shadow-none min-h-[140px]"
          style={appLaunchesHeight.style}
        >
          <CardHeader className="space-y-3">
            <div className="w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </div>
            <div className="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </CardHeader>
        </Card>
        <Card 
          ref={avgTimeHeight.ref}
          className="@container/card rounded-xl border shadow-none min-h-[140px]"
          style={avgTimeHeight.style}
        >
          <CardHeader className="space-y-3">
            <div className="w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </div>
            <div className="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </CardHeader>
        </Card>
        <Card 
          ref={scenarioLaunchesHeight.ref}
          className="@container/card rounded-xl border shadow-none min-h-[140px]"
          style={scenarioLaunchesHeight.style}
        >
          <CardHeader className="space-y-3">
            <div className="w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </div>
            <div className="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (data.error) {
    return (
      <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
        <Card className="@container/card col-span-full rounded-xl border shadow-none">
          <CardHeader>
            <CardDescription>Ошибка загрузки аналитики</CardDescription>
            <CardTitle className="text-lg text-red-600 dark:text-red-400">
              {data.error}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <TooltipProvider>
    <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4">
      <Card 
        ref={uniqueUsersHeight.ref}
        className="@container/card rounded-xl border shadow-none"
        style={uniqueUsersHeight.style}
      >
        <CardHeader className="space-y-3">
          <CardTitle className="text-3xl font-bold tabular-nums">
            {formatNumber(data.uniqueUsers)}
          </CardTitle>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <CardDescription className="text-sm font-medium">
                  Уникальные пользователи {data.periodDescription}
                </CardDescription>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs sm:max-w-sm text-sm leading-snug">
              Количество уникальных пользователей, которые запускали приложение {data.periodDescription}.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-sm cursor-default">
                {uniqueUsersChange.positive === false ? (
                  <TrendingDown className={`h-3 w-3 ${uniqueUsersChange.color}`} />
                ) : (
                  <TrendingUp className={`h-3 w-3 ${uniqueUsersChange.color}`} />
                )}
                <span className={`${uniqueUsersChange.color} font-medium`}>{uniqueUsersChange.text}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-sm leading-snug">
              По сравнению с предыдущим периодом ({data.prevUniqueUsers?.toLocaleString('ru-RU') ?? "—"})
            </TooltipContent>
          </Tooltip>
        </CardHeader>
      </Card>
      
      <Card 
        ref={appLaunchesHeight.ref}
        className="@container/card rounded-xl border shadow-none"
        style={appLaunchesHeight.style}
      >
        <CardHeader className="space-y-3">
          <CardTitle className="text-3xl font-bold tabular-nums">
            {formatNumber(data.appLaunches)}
          </CardTitle>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <CardDescription className="text-sm font-medium">
                  Запуски приложения {data.periodDescription}
                </CardDescription>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs sm:max-w-sm text-sm leading-snug">
              Общее количество запусков приложения всеми пользователями {data.periodDescription}.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-sm cursor-default">
                {appLaunchesChange.positive === false ? (
                  <TrendingDown className={`h-3 w-3 ${appLaunchesChange.color}`} />
                ) : (
                  <TrendingUp className={`h-3 w-3 ${appLaunchesChange.color}`} />
                )}
                <span className={`${appLaunchesChange.color} font-medium`}>{appLaunchesChange.text}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-sm leading-snug">
              По сравнению с предыдущим периодом ({data.prevAppLaunches?.toLocaleString('ru-RU') ?? "—"})
            </TooltipContent>
          </Tooltip>
        </CardHeader>
      </Card>
      
      <Card 
        ref={avgTimeHeight.ref}
        className="@container/card rounded-xl border shadow-none"
        style={avgTimeHeight.style}
      >
        <CardHeader className="space-y-3">
          <CardTitle className="text-3xl font-bold tabular-nums">
            {formatTime(data.avgTime)}
          </CardTitle>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <CardDescription className="text-sm font-medium">
                  Среднее время в приложении
                </CardDescription>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs sm:max-w-sm text-sm leading-snug">
              Среднее время проведенное пользователями в приложении {data.periodDescription}.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-sm cursor-default">
                {avgTimeChange.positive === false ? (
                  <TrendingDown className={`h-3 w-3 ${avgTimeChange.color}`} />
                ) : (
                  <TrendingUp className={`h-3 w-3 ${avgTimeChange.color}`} />
                )}
                <span className={`${avgTimeChange.color} font-medium`}>{avgTimeChange.text}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-sm leading-snug">
              По сравнению с предыдущим периодом ({formatTime(data.prevAvgTime ?? null)})
            </TooltipContent>
          </Tooltip>
        </CardHeader>
      </Card>
      
      <Card 
        ref={scenarioLaunchesHeight.ref}
        className="@container/card rounded-xl border shadow-none"
        style={scenarioLaunchesHeight.style}
      >
        <CardHeader className="space-y-3">
          <CardTitle className="text-3xl font-bold tabular-nums">
            {formatNumber(data.scenarioLaunches)}
          </CardTitle>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <Play className="h-4 w-4 text-muted-foreground" />
                <CardDescription className="text-sm font-medium">
                  Запуски процедур {data.periodDescription}
                </CardDescription>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs sm:max-w-sm text-sm leading-snug">
              Количество запусков процедур всеми пользователями {data.periodDescription}.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-sm cursor-default">
                {scenarioLaunchesChange.positive === false ? (
                  <TrendingDown className={`h-3 w-3 ${scenarioLaunchesChange.color}`} />
                ) : (
                  <TrendingUp className={`h-3 w-3 ${scenarioLaunchesChange.color}`} />
                )}
                <span className={`${scenarioLaunchesChange.color} font-medium`}>{scenarioLaunchesChange.text}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-sm leading-snug">
              По сравнению с предыдущим периодом ({data.prevScenarioLaunches?.toLocaleString('ru-RU') ?? "—"})
            </TooltipContent>
          </Tooltip>
        </CardHeader>
      </Card>
    </div>
    </TooltipProvider>
  )
} 